import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Invitations API
export const invitationsAPI = {
  create: (data) => api.post('/invitations', data),
  getAll: () => api.get('/invitations'),
  validate: (token) => api.get(`/invitations/validate?token=${token}`),
  accept: (data) => api.post('/invitations/accept', data),
  resend: (id) => api.post(`/invitations/${id}/resend`),
  cancel: (id) => api.delete(`/invitations/${id}`),
};

// Clienti API
export const clientiAPI = {
  getAll: () => api.get('/clienti'),
  getById: (id) => api.get(`/clienti/${id}`),
  create: (data) => api.post('/clienti', data),
  update: (id, data) => api.put(`/clienti/${id}`, data),
  delete: (id) => api.delete(`/clienti/${id}`),
};

// Commesse API
export const commesseAPI = {
  getAll: () => api.get('/commesse'),
  getById: (id) => api.get(`/commesse/${id}`),
  create: (data) => api.post('/commesse', data),
  update: (id, data) => api.put(`/commesse/${id}`, data),
  delete: (id) => api.delete(`/commesse/${id}`),
  chiudi: (id) => api.put(`/commesse/${id}/chiudi`),
  riapri: (id) => api.put(`/commesse/${id}/riapri`),
  // Budget
  addBudgetRisorsa: (id, data) => api.post(`/commesse/${id}/budget/risorse`, data),
  addBudgetEsterno: (id, data) => api.post(`/commesse/${id}/budget/esterni`, data),
  addBudgetTrasferta: (id, data) => api.post(`/commesse/${id}/budget/trasferte`, data),
  deleteBudgetRisorsa: (id, budgetId) => api.delete(`/commesse/${id}/budget/risorse/${budgetId}`),
  deleteBudgetEsterno: (id, budgetId) => api.delete(`/commesse/${id}/budget/esterni/${budgetId}`),
  deleteBudgetTrasferta: (id, budgetId) => api.delete(`/commesse/${id}/budget/trasferte/${budgetId}`),
  // Fasi
  addFase: (id, data) => api.post(`/commesse/${id}/fasi`, data),
  deleteFase: (id, faseId) => api.delete(`/commesse/${id}/fasi/${faseId}`),
  // Team
  getTeam: (id) => api.get(`/commesse/${id}/team`),
  addTeamMember: (id, risorsaId) => api.post(`/commesse/${id}/team/${risorsaId}`),
  removeTeamMember: (id, risorsaId) => api.delete(`/commesse/${id}/team/${risorsaId}`),
};

// Risorse API
export const risorseAPI = {
  getAll: () => api.get('/risorse'),
  getById: (id) => api.get(`/risorse/${id}`),
  create: (data) => api.post('/risorse', data),
  update: (id, data) => api.put(`/risorse/${id}`, data),
  delete: (id) => api.delete(`/risorse/${id}`),
};

// Users Management API (Admin only)
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  updateRole: (id, ruolo) => api.put(`/users/${id}/role`, { ruolo }),
  updateStatus: (id, is_active) => api.put(`/users/${id}/status`, { is_active }),
  delete: (id) => api.delete(`/users/${id}`),
};

// Ruoli API (Ruoli professionali per risorse)
export const ruoliAPI = {
  getAll: () => api.get('/ruoli'),
  create: (data) => api.post('/ruoli', data),
  update: (id, data) => api.put(`/ruoli/${id}`, data),
  delete: (id) => api.delete(`/ruoli/${id}`),
};

// User Roles API (Ruoli utente di sistema con permessi)
export const userRolesAPI = {
  getAll: () => api.get('/user-roles'),
  getPermissions: (roleCode) => api.get(`/user-roles/${roleCode}/permissions`),
  getMyPermissions: () => api.get('/my-permissions'),
  getSystemRoles: () => api.get('/system-roles'),
  initSystemRoles: () => api.post('/init-system-roles'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Timesheet API
export const timesheetAPI = {
  getAll: (params = {}) => api.get('/timesheet', { params }),
  create: (data) => api.post('/timesheet', data),
  update: (id, data) => api.put(`/timesheet/${id}`, data),
  delete: (id) => api.delete(`/timesheet/${id}`),
  approve: (data) => api.post('/timesheet/approve', data),
  getDaySummary: (data, risorsaId = null) => api.get(`/timesheet/summary/day/${data}`, { params: { risorsa_id: risorsaId } }),
  getWeekSummary: (settimanaInizio, risorsaId = null) => api.get('/timesheet/summary/week', { params: { settimana_inizio: settimanaInizio, risorsa_id: risorsaId } }),
  getPendingApproval: (commessaId = null) => api.get('/timesheet/pending-approval', { params: { commessa_id: commessaId } }),
};

// Trasferte Consuntivo API
export const trasferteAPI = {
  getAll: (params = {}) => api.get('/trasferte-consuntivo', { params }),
  create: (data) => api.post('/trasferte-consuntivo', data),
  update: (id, data) => api.put(`/trasferte-consuntivo/${id}`, data),
  delete: (id) => api.delete(`/trasferte-consuntivo/${id}`),
  getDisponibili: (commessaId) => api.get(`/commesse/${commessaId}/trasferte-disponibili`),
};

// Costi Esterni Consuntivo API
export const costiEsterniAPI = {
  getAll: (params = {}) => api.get('/costi-esterni-consuntivo', { params }),
  create: (data) => api.post('/costi-esterni-consuntivo', data),
  update: (id, data) => api.put(`/costi-esterni-consuntivo/${id}`, data),
  delete: (id) => api.delete(`/costi-esterni-consuntivo/${id}`),
  getDisponibili: (commessaId) => api.get(`/commesse/${commessaId}/costi-esterni-disponibili`),
};

// Cost Approval API
export const costiApprovalAPI = {
  approve: (data) => api.post('/costi/approve', data),
  getPending: (tipo = null, commessaId = null) => api.get('/costi/pending-approval', { params: { tipo, commessa_id: commessaId } }),
};

// Fatture / Ricavi API
export const fattureAPI = {
  getAll: (params = {}) => api.get('/fatture', { params }),
  getById: (id) => api.get(`/fatture/${id}`),
  create: (data) => api.post('/fatture', data),
  update: (id, data) => api.put(`/fatture/${id}`, data),
  delete: (id) => api.delete(`/fatture/${id}`),
  getSummary: (commessaId = null) => api.get('/fatture/summary/ricavi', { params: { commessa_id: commessaId } }),
  getAgingReport: () => api.get('/fatture/report/aging'),
  calcolaRicaviTM: (commessaId, dataDa = null, dataA = null) => 
    api.get(`/fatture/calcolo-tm/${commessaId}`, { params: { data_da: dataDa, data_a: dataA } }),
  getAlertScadenze: (giorni = 7) => api.get('/fatture/alerts/scadenze', { params: { giorni } }),
};

// Permissions API
export const permissionsAPI = {
  getStructure: () => api.get('/permissions/structure'),
  getAllRoles: () => api.get('/permissions/roles'),
  getRolePermissions: (roleName) => api.get(`/permissions/role/${roleName}`),
  getMyPermissions: () => api.get('/permissions/my'),
  updateRolePermissions: (roleName, permissions) => api.put(`/permissions/role/${roleName}`, permissions),
  resetRolePermissions: (roleName) => api.post(`/permissions/reset/${roleName}`),
  getAvailableRoles: () => api.get('/permissions/available-roles'),
};

// Backup API (Admin only)
export const backupAPI = {
  exportAll: () => api.get('/backup/export'),
};

export default api;
